import type { Price } from '@/components/subscriptions/types/price';
import type { Product } from '@/components/subscriptions/types/product';

export type PublicProductPrice = {
  priceId: string;
  amount: number | null;
  currency: string | null;
  interval: 'month' | 'year' | 'one_time' | null;
};

export type PublicProduct = {
  productId: string;
  name: string;
  description: string;
  prices: PublicProductPrice[];
};

function isActiveProduct(product: Product): boolean {
  return product.active !== false;
}

function isActivePrice(price: Price): boolean {
  return price.isActive !== false;
}

function toPublicPrice(price: Price): PublicProductPrice {
  return {
    priceId: price.id,
    amount: price.priceAmount,
    currency: price.priceCurrency,
    interval: price.priceInterval,
  };
}

export function buildPublicProductCatalog(
  products: Product[],
  prices: Price[],
): PublicProduct[] {
  const activePricesByProduct = new Map<string, PublicProductPrice[]>();

  for (const price of prices) {
    if (!isActivePrice(price)) {
      continue;
    }
    const existing = activePricesByProduct.get(price.productId) ?? [];
    existing.push(toPublicPrice(price));
    activePricesByProduct.set(price.productId, existing);
  }

  return products
    .filter(isActiveProduct)
    .map((product) => {
      const productPrices = activePricesByProduct.get(product.id) ?? [];
      if (productPrices.length === 0) {
        return null;
      }
      return {
        productId: product.id,
        name: product.name,
        description: product.description ?? '',
        prices: productPrices,
      };
    })
    .filter((product): product is PublicProduct => product !== null);
}

/** /welcome "Get started": signed-in buyers go straight to the purchase confirmation. */
export function getStartedHref(priceId: string, isAuthenticated: boolean): string {
  const purchasePath = `/purchase?priceId=${encodeURIComponent(priceId)}`;
  return isAuthenticated ? purchasePath : `/login?returnTo=${encodeURIComponent(purchasePath)}`;
}

export function resolveRootRedirect(isAuthenticated: boolean): '/login' | '/dashboard' {
  return isAuthenticated ? '/dashboard' : '/login';
}

export function resolveWelcomeRedirect(params: {
  isAuthenticated: boolean;
  hasAppAccess: boolean;
  checkoutCanceled: boolean;
  browseIntent?: boolean;
}): '/dashboard' | null {
  if (!params.isAuthenticated) {
    return null;
  }
  if (params.checkoutCanceled) {
    return null;
  }
  // A role-holder (e.g. an administrator with role-granted access) following the Settings
  // card's "Choose a plan" link came here to browse, not to be bounced back to /dashboard.
  if (params.browseIntent) {
    return null;
  }
  // Nothing to bounce to without app access — show the catalog instead of a
  // round trip through /dashboard's own redirect to /signup-required.
  if (!params.hasAppAccess) {
    return null;
  }
  return '/dashboard';
}
